import { Formik, FormikValues } from 'formik'
import close from '../../assets/icons/close.svg'
import * as yup from 'yup'
import styles from './CreateClientModal.module.css'
import s from '../../styles/common/Form.module.css'
import CustomInput from '../../components/UI/CustomInput/CustomInput'
import CustomButton from '../../components/UI/CustomButton/CustomButton'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import { ChangeEvent, MouseEvent, useEffect, useState } from 'react'
import p from '../../components/UI/CustomInput/CustomInput.module.css'
import InputMask from 'react-input-mask'
import { getDaDataCity, getDaDataCompany } from '../../redux/actions/daDataAction'
import { useMediaQuery } from 'react-responsive'
import { useDebounce } from '../../hooks/useDebounce'
import { clearDadata } from '../../redux/reducers/daData/daDataSlice'
import { createClient } from '../../redux/actions/clientsAction'
import { useToast } from '../../context/ToastContext'

export interface Suggestion {
  data: {
    city: string
  }
}

type CreateClientModalProps = {
  isOpen: boolean
  onClose: () => void
}

function CreateClientModal({ isOpen, onClose }: CreateClientModalProps) {
  const [city, setCity] = useState<string>('')
  const [citySuggestions, setCitySuggestions] = useState<Suggestion[]>([])
  const [inn, setInn] = useState<string>('')
  const [loader, setLoader] = useState(false)
  const [isFocused, setIsFocused] = useState(true)
  const [clientType, setClientType] = useState('individual')
  const debounced = useDebounce(inn, 1000)

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })

  const { isLoading, token } = useAppSelector((state) => state.auth)
  const { error } = useAppSelector((state) => state.clients)
  const { daDataCity, daDataCompany } = useAppSelector((state) => state.daData)

  const [submittedValues, setSubmittedValues] = useState<any>(null)

  const [roleError, setRoleError] = useState('')
  const [cityError, setCityError] = useState('')

  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()

  const _handleBlur = () => {
    setIsFocused(false)
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const validationSchema = yup.object().shape({
    name: yup.string().required('Поле является обязательным'),
    address: yup.string().required('Поле является обязательным'),
    fullName: yup.string().required('Поле является обязательным'),
    phone: yup
      .string()
      .required('Поле является обязательным')
      .matches(
        /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/,
        'Неверный формат',
      ),
  })

  const handleCityInputChange = async (value: string) => {
    setCity(value)

    if (value?.trim().length >= 1) {
      await dispatch(getDaDataCity({ query: city }))
    } else {
      setCitySuggestions([])
    }
  }

  const handleInnInputChange = async (value: string) => {
    const numericValue = value.replace(/\D/g, '')
    setInn(numericValue)
  }

  const handleSelectSuggestion = (selectedCity: string) => {
    setCity(selectedCity)
    setCitySuggestions([])
  }

  useEffect(() => {
    if (clientType === 'individual') {
      setInn('')
      setSubmittedValues(null)
      dispatch(clearDadata())
    } else if (clientType === 'legal') {
      setInn('')
      setSubmittedValues(null)
      dispatch(clearDadata())
    }
  }, [clientType, dispatch])

  useEffect(() => {
    if (inn.length === 0 || inn.length < 10) {
      setSubmittedValues(null)
      dispatch(clearDadata())
    }
  }, [inn, dispatch])

  const handleSubmitHandler = async (values: FormikValues) => {
    setSubmittedValues({ ...values, inn, city })
    const mobileWithoutSymbols = `+${values.phone.replace(/[\D]+/g, '')}`

    if (clientType === 'legal' && inn.length > 0) {
      if (!daDataCompany?.suggestions || daDataCompany.suggestions.length === 0) {
        showError('Организация с таким ИНН не найдена')
        return
      }

      const companyData = daDataCompany.suggestions[0]?.data
      if (!companyData || !companyData.name?.full) {
        showError('Не удалось получить данные организации по ИНН')
        return
      }
    }

    const data = {
      role: 'client',
      contactPhone: mobileWithoutSymbols,
      city: city,
      fullName: values?.fullName,
      phone: mobileWithoutSymbols,
      legalAddress: values?.address,
      contactFullName: values.fullName,
      organizationName: values?.name,
      inn: inn,
      mailingAddress: values?.email,
      email: values.email,
      type: clientType,
      kpp: values?.kpp,
      ogrn: values?.ogrn,
      bankName: values?.bankName,
      signerPosition: values?.signerPostition,
    }

    try {
      await dispatch(createClient({ data, token })).unwrap()
      onClose()
      showSuccess('Клиент успешно создан')
    } catch (error) {
      showError('Произошла ошибка при создании клиента')
    }
  }

  useEffect(() => {
    if (debounced && debounced.length >= 10) {
      setLoader(true)

      dispatch(getDaDataCompany({ query: debounced }))

      const timer = setTimeout(() => {
        setLoader(false)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [debounced, dispatch])

  useEffect(() => {
    if (daDataCompany?.suggestions && daDataCompany.suggestions.length > 0 && inn.length >= 10) {
      const currentData = daDataCompany.suggestions[0]?.data
      if (currentData && (currentData.name?.full || currentData.address?.value)) {
        if (currentData.address?.data?.city) {
          setCity(currentData.address.data.city)
        }

        const newValues = {
          name: currentData.name?.full || '',
          address: currentData.address?.value || '',
          city: currentData.address?.data?.city || '',
          kpp: currentData.kpp || '',
          ogrn: currentData.ogrn || '',
          fullName: currentData.management?.name || '',
          phone: currentData.phones || '',
          signerPostition: currentData.management?.post || '',
          email: '',
          password: '',
          type: 'legal',
          bankName: '',
        }
        const hasChanged =
          !submittedValues ||
          submittedValues.name !== newValues.name ||
          submittedValues.address !== newValues.address ||
          submittedValues.city !== newValues.city ||
          submittedValues.kpp !== newValues.kpp ||
          submittedValues.ogrn !== newValues.ogrn ||
          submittedValues.fullName !== newValues.fullName ||
          submittedValues.phone !== newValues.phone ||
          submittedValues.signerPostition !== newValues.signerPostition

        if (hasChanged) {
          setSubmittedValues(newValues)
        }
      }
    }
  }, [daDataCompany, inn, submittedValues])

  useEffect(() => {
    setCitySuggestions(daDataCity)
  }, [daDataCity])

  useEffect(() => {
    if (submittedValues?.city && submittedValues.city !== city) {
      setCity(submittedValues.city)
    }
  }, [submittedValues, city])

  if (!isOpen) return null

  return (
    <div>
      <div className={styles.registrationModalOverlay}>
        <div className={styles.registrationModal}>
          <div className={styles.titleBlock}>
            <h1 className={styles.title}>Создание клиента</h1>
            <img onClick={onClose} className={styles.close} src={close} alt='close' />
          </div>
          <div className={styles.clientGroup}>
            <div className={styles.inputContainer}>
              <h4 className={styles.subtitle}>Клиент</h4>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type='radio'
                    name='clientType'
                    value='individual'
                    checked={clientType === 'individual'}
                    onChange={() => setClientType('individual')}
                    className={styles.radioInput}
                  />
                  <span className={styles.customRadio}></span>
                  Физ.лицо
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type='radio'
                    name='clientType'
                    value='legal'
                    checked={clientType === 'legal'}
                    onChange={() => setClientType('legal')}
                    className={styles.radioInput}
                  />
                  <span className={styles.customRadio}></span>
                  Юр.лицо
                </label>
              </div>
            </div>
          </div>

          {isOpen && clientType == 'legal' ? (
            <div>
              <div className={styles.inputsContainer}>
                <Formik
                  key={submittedValues ? `with-data-${inn}` : 'without-data'}
                  initialValues={
                    submittedValues || {
                      name: '',
                      address: '',
                      kpp: '',
                      ogrn: '',
                      fullName: '',
                      phone: '',
                      signerPostition: '',
                      email: '',
                      password: '',
                      city: '',
                      type: 'legal',
                      bankName: '',
                    }
                  }
                  validateOnBlur
                  onSubmit={(values) => handleSubmitHandler(values)}
                  validationSchema={validationSchema}
                >
                  {(props) => {
                    const {
                      values,
                      touched,
                      errors,
                      isValid,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                    } = props

                    return (
                      <>
                        <div className={styles.formContainer}>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>ИНН</h4>
                            <div className={styles.inputContainerWithLoader}>
                              <CustomInput
                                type='text'
                                placeholder='ИНН'
                                required
                                onFocus={handleFocus}
                                onBlur={_handleBlur}
                                onChange={(e) => handleInnInputChange(e.target.value)}
                                value={inn}
                                className={
                                  inn.length > 0 &&
                                  inn.length >= 10 &&
                                  (!daDataCompany?.suggestions ||
                                    daDataCompany.suggestions.length === 0)
                                    ? `${s.inputError} ${styles.input}`
                                    : styles.input
                                }
                              />
                              {loader && inn.length >= 10 && (
                                <div className={styles.loader}>Загрузка...</div>
                              )}
                              {inn.length > 0 &&
                                inn.length >= 10 &&
                                daDataCompany?.suggestions &&
                                daDataCompany.suggestions.length > 0 && (
                                  <div className={styles.successMessage}>
                                    ✓ Данные организации загружены
                                  </div>
                                )}
                              {inn.length > 0 &&
                                inn.length >= 10 &&
                                (!daDataCompany?.suggestions ||
                                  daDataCompany.suggestions.length === 0) &&
                                !loader && (
                                  <div className={s.errorContainer}>
                                    <p className={s.errorValidation}>
                                      Организация с таким ИНН не найдена
                                    </p>
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>КПП</h4>
                            <CustomInput
                              type='text'
                              placeholder='КПП'
                              name='kpp'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.kpp}
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>ОГРН</h4>
                            <CustomInput
                              type='text'
                              placeholder='ОГРН'
                              name='ogrn'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.ogrn}
                              className={styles.input}
                            />
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Название организации</h4>
                            <div className={styles.inputContainerWithError}>
                              <CustomInput
                                type='text'
                                placeholder='Название организации'
                                name='name'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.name}
                                className={styles.input}
                              />
                              {errors.name && (
                                <div className={s.errorContainer}>
                                  {touched.name && errors.name && (
                                    <p className={s.errorValidation}>{errors.name as string}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Город</h4>
                            <div className={styles.inputContainerWithError}>
                              <CustomInput
                                type='text'
                                placeholder='Город'
                                name='city'
                                required
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                  const value = e.target.value
                                  setCity(value)
                                  handleCityInputChange(value)
                                }}
                                value={city}
                                className={styles.input}
                              />
                              <ul className={styles.suggestionsDropdown}>
                                {citySuggestions.map((suggestion: Suggestion, index: number) => (
                                  <li
                                    key={index}
                                    onClick={() => handleSelectSuggestion(suggestion.data.city)}
                                  >
                                    {suggestion.data.city}
                                  </li>
                                ))}
                              </ul>
                              {errors.city && (
                                <div className={s.errorContainer}>
                                  {touched.city && errors.city && (
                                    <p className={s.errorValidation}>{errors.city as string}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Адрес</h4>
                            <div className={styles.inputContainerWithError}>
                              <CustomInput
                                type='text'
                                placeholder='Адрес'
                                name='address'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.address}
                                className={styles.input}
                              />
                              {errors.address && (
                                <div className={s.errorContainer}>
                                  {touched.address && errors.address && (
                                    <p className={s.errorValidation}>{errors.address as string}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>ФИО контактного лица</h4>
                            <div className={styles.inputContainerWithError}>
                              <CustomInput
                                type='text'
                                placeholder='ФИО контактного лица'
                                name='fullName'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.fullName}
                                className={styles.input}
                              />
                              {errors.fullName && (
                                <div className={s.errorContainer}>
                                  {touched.fullName && errors.fullName && (
                                    <p className={s.errorValidation}>{errors.fullName as string}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Роль Контактного Лица</h4>
                            <CustomInput
                              type='text'
                              placeholder='Роль Контактного Лица'
                              name='signerPostition'
                              required
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.signerPostition}
                              className={styles.input}
                            />
                          </div>

                          <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Номер телефона</h4>
                            <div className={styles.inputContainerWithError}>
                              <InputMask
                                mask='+7 (999) 999 9999'
                                type='phone'
                                placeholder='Номер телефона'
                                name='phone'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.phone}
                                className={
                                  errors.phone && touched.phone
                                    ? `${s.inputError} ${p.input}`
                                    : `${s.input} ${p.input}`
                                }
                              />
                              {errors.phone && (
                                <div className={s.errorContainer}>
                                  {touched.phone && errors.phone && (
                                    <p className={s.errorValidation}>{errors.phone as string}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Электронная почта</h4>
                            <div className={styles.inputContainerWithError}>
                              <CustomInput
                                type='text'
                                placeholder='Электронная почта'
                                name='email'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.email?.trim()}
                                className={styles.input}
                              />
                              {errors.email && (
                                <div className={s.errorContainer}>
                                  {touched.email && errors.email && (
                                    <p className={s.errorValidation}>{errors.email as string}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div> */}
                          {/* <div className={styles.inputContainer}>
                            <h4 className={styles.subtitle}>Наименование Банка</h4>
                            <CustomInput
                              type='text'
                              placeholder='Наименование Банка'
                              name='bankName'
                              required
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values?.bankName}
                              className={styles.input}
                            />
                          </div> */}
                        </div>
                        <div className={styles.footerContainer}>
                          <>
                            {roleError && (
                              <p className={`${s.errorValidation} ${s.networkError}`}>
                                {roleError}
                              </p>
                            )}
                            {cityError && (
                              <p className={`${s.errorValidation} ${s.networkError}`}>
                                {cityError}
                              </p>
                            )}
                            {error && (
                              <p className={`${s.errorValidation} ${s.networkError}`}>
                                {error || 'произошла серверная ошибка'}
                              </p>
                            )}

                            <CustomButton
                              title='Создать'
                              disabled={!isValid}
                              onClick={() => handleSubmit()}
                              type='submit'
                              className={styles.submitBtn}
                            />
                          </>
                        </div>
                      </>
                    )
                  }}
                </Formik>
              </div>
            </div>
          ) : (
            isOpen && (
              <div>
                <div className={styles.inputsContainer}>
                  <Formik
                    initialValues={
                      submittedValues || {
                        fullName: '',
                        phone: '',
                        email: '',
                        type: 'individual',
                      }
                    }
                    validateOnBlur
                    onSubmit={(values) => handleSubmitHandler(values)}
                  >
                    {(props) => {
                      const {
                        values,
                        touched,
                        errors,
                        isValid,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                      } = props

                      return (
                        <>
                          <div className={styles.formContainer}>
                            <div className={styles.inputContainer}>
                              <h4 className={styles.subtitle}>ФИО</h4>
                              <CustomInput
                                type='text'
                                placeholder='ФИО'
                                name='fullName'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.fullName}
                                className={styles.input}
                              />
                            </div>
                            <div
                              style={{ width: isMobile ? '100%' : '103%' }}
                              className={styles.inputContainer}
                            >
                              <h4 className={styles.subtitle}>Номер телефона</h4>
                              <InputMask
                                style={{ width: isMobile ? '100%' : '356px' }}
                                mask='+7 (999) 999 9999'
                                type='phone'
                                placeholder='Номер телефона'
                                name='phone'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.phone}
                                className={
                                  errors.phone && touched.phone
                                    ? `${s.inputError} ${p.input}`
                                    : `${s.input} ${p.input}`
                                }
                              />

                              {touched.phone && errors.phone && (
                                <div className={s.errorContainer}>
                                  <p className={s.errorValidation}>{errors.phone as string}</p>
                                </div>
                              )}
                            </div>
                            {/* <div className={styles.inputContainer}>
                              <h4 className={styles.subtitle}>Электронная почта/Логин</h4>
                              <CustomInput
                                type='text'
                                placeholder='Электронная почта'
                                name='email'
                                required
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values?.email?.trim()}
                                className={styles.input}
                              />
                            </div> */}
                          </div>
                          <div className={styles.footerContainer}>
                            <CustomButton
                              title='Создать'
                              disabled={!isValid}
                              onClick={() => handleSubmit()}
                              type='submit'
                              className={styles.submitBtn}
                            />
                          </div>
                        </>
                      )
                    }}
                  </Formik>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}

export default CreateClientModal
